// frontend/src/components/VerifyInvoiceSequence.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import api from '../utils/api';

const VerifyInvoiceSequence = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [facturaInicial, setFacturaInicial] = useState('');
  const [facturaFinal, setFacturaFinal] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Validar permisos
  useEffect(() => {
    if (!loading && (!user || !user.is_notario)) {
      toast.error('No tienes permisos para acceder a esta página.', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Cargar días disponibles
  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response = await api.get('/api/audit/document-days/', {
          params: { page: 1, page_size: 100 }
        });
        setDays(response.data.days || []);
        if (response.data.days && response.data.days.length > 0) {
          setSelectedDay(response.data.days[0]);
        }
      } catch (error) {
        toast.error('Error al cargar los días disponibles.', { position: 'top-right', autoClose: 5000 });
        setError('Error al cargar los días disponibles.');
      }
    };
    if (!loading && user && user.is_notario) {
      fetchDays();
    }
  }, [user, loading]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedDay || !facturaInicial || !facturaFinal) {
      toast.error('Por favor, completa todos los campos.', { position: 'top-right', autoClose: 3000 });
      return;
    }

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await api.post('/api/audit/verify-invoice-sequence/', {
        fecha_documento: selectedDay,
        factura_inicial: facturaInicial,
        factura_final: facturaFinal,
      });
      setResult(response.data);
      toast.success('Verificación completada.', { position: 'top-right', autoClose: 3000 });
    } catch (error) {
      setError(error.response?.data?.error || 'Error al verificar la secuencia de facturas.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedDay, facturaInicial, facturaFinal]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Verificar Secuencia de Facturas</h1>
        </div>
        <Link
          to="/dashboard/notario"
          className="bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all duration-300"
          aria-label="Volver al Dashboard"
        >
          Volver al Dashboard
        </Link>
      </header>

      <main className="flex-1">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-6 rounded-xl shadow-2xl"
        >
          <h2 className="text-xl sm:text-2xl font-semibold mb-6">Verificar Secuencia de Facturas</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label htmlFor="fecha_documento" className="block text-sm sm:text-lg font-semibold mb-2">
                  Día de los Trámites
                </label>
                <select
                  id="fecha_documento"
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value)}
                  className="w-full p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  required
                >
                  <option value="">Seleccione un día</option>
                  {days.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="factura_inicial" className="block text-sm sm:text-lg font-semibold mb-2">
                  Factura Inicial (ej. 001-010-000118587)
                </label>
                <input
                  type="text"
                  id="factura_inicial"
                  value={facturaInicial}
                  onChange={(e) => setFacturaInicial(e.target.value)}
                  placeholder="001-010-000118587"
                  className="w-full p-3 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  required
                  aria-label="Factura inicial"
                />
              </div>
              <div>
                <label htmlFor="factura_final" className="block text-sm sm:text-lg font-semibold mb-2">
                  Factura Final (ej. 001-010-000118662)
                </label>
                <input
                  type="text"
                  id="factura_final"
                  value={facturaFinal}
                  onChange={(e) => setFacturaFinal(e.target.value)}
                  placeholder="001-010-000118662"
                  className="w-full p-3 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  required
                  aria-label="Factura final"
                />
              </div>
            </div>
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500"
              >
                {error}
              </motion.div>
            )}
            <motion.button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-notaria-yellow text-notaria-blue-1 font-bold py-2 rounded-lg hover:bg-notaria-red hover:text-white transition-all ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
              aria-label="Verificar secuencia de facturas"
            >
              {isLoading ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
                  Verificando...
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="w-5 h-5 inline mr-2" />
                  Verificar Secuencia
                </>
              )}
            </motion.button>
          </form>

          {result && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-6 bg-notaria-blue-2 p-4 rounded-lg"
            >
              <h3 className="text-lg font-semibold mb-3">Resultado de la Verificación</h3>
              <p><span className="font-medium">Total Esperadas:</span> {result.total_esperadas}</p>
              <p><span className="font-medium">Total Presentes:</span> {result.total_presentes}</p>
              <p><span className="font-medium">Total Faltantes:</span> {result.total_faltantes}</p>
              {result.total_faltantes > 0 ? (
                <div className="mt-3">
                  <p className="font-medium text-red-400">Facturas Faltantes:</p>
                  <ul className="list-disc list-inside mt-2">
                    {result.facturas_faltantes.map((factura, index) => (
                      <li key={index} className="text-sm">{factura}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="mt-2 text-green-400">No faltan facturas en la secuencia.</p>
              )}
            </motion.div>
          )}
        </motion.div>
      </main>

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default VerifyInvoiceSequence;